<script lang="ts" setup>
import { computed } from "vue";
import { OrderFreightState } from "@/constants/order";
import type { TripRouteFreight, TripRouteStopFreight } from "@/types/trip";

const props = defineProps<{
  freight: TripRouteStopFreight;
  unusedRouteFreights: TripRouteFreight[];
  isPickup: boolean;
}>();

const isSelected = computed(() => {
  return !props.unusedRouteFreights.some((item) => item.id === props.freight.id);
});

const freightClass = computed(() => {
  if (props.freight.isDeleted) {
    return "freight--deleted";
  }

  if (props.isPickup && !isSelected.value) {
    return "freight--not-selected";
  }

  if (props.freight.state === OrderFreightState.updated) {
    return "freight--updated";
  }

  if (props.freight.state === OrderFreightState.new) {
    return "freight--new";
  }

  return "";
});

const shouldShowNotSelected = computed(() => {
  return props.isPickup && !isSelected.value && !props.freight.isDeleted;
});
</script>

<template>
  <div :class="freightClass" class="freight">
    <span class="freight__name">
      {{ freight.name }}
    </span>

    <span v-if="shouldShowNotSelected" class="text-orange-darken-3 font-weight-500">Not selected</span>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/style/color";

.freight {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.freight__name {
  position: relative;
  width: max-content;

  &:after {
    position: absolute;
    content: "";
    display: none;
    top: 50%;
    transform: translateY(-50%);
    width: 6px;
    height: 6px;
    border-radius: 50%;
    text-decoration: none !important;
    right: -14px;
  }
}

.freight--deleted {
  text-decoration-line: line-through;
  text-decoration-color: $grey-lighter-contrast;
  color: $grey-lighter-contrast !important;

  .freight__name {
    &:after {
      display: block;
      background-color: $grey-lighter-contrast;
      color: $grey-lighter-contrast;
    }
  }
}

.freight--updated {
  .freight__name {
    &:after {
      display: block;
      background-color: orange;
    }
  }
}

.freight--new {
  .freight__name {
    &:after {
      display: block;
      background-color: green;
    }
  }
}

.freight--not-selected {
  color: $warning;
}
</style>
